import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter } from '@angular/core';
import { take, tap } from 'rxjs/operators';

import { environment as ENV } from '@environment';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import {
  AssistanceSearchInclusionDetail, AssistanceSearchResponse, AssistanceSearchResult
} from '@shared/travel-assistance/entities/booking-assistance';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { AssetChubbBookingBanner } from '@asset-chubb-booking-banner';
import { AssetChubbBookingBannerInterface } from '@app/entities/asset-chubb-booking-banner';
import { CurrencyFormatPipe } from '@app/pipes/currency-format.pipe';
import { ANIMATIONS, TIME_TO_SLIDE } from '@app/constants/animations.constants';
import { assistanceValuesDetail } from '@shared/travel-assistance/mappers/assistance-highlighted.mapper';
import { AmplitudeEventParams } from '@amplitude/interfaces/amplitude-events';
import { assistanceSearchAmplitudePropsMapper } from '@app/modules/core/mappers/assistance-amplitude.mapper';
import { loaderColors } from '@ux/entities/loader-colors';
import { Countries } from '@ux/entities/country.enum';
import { isNullOrUndefined } from '@app/util/util';
import { AVO_EVENTS } from '@app/modules/avo/events/avo.events';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { SearchBy } from '@app/modules/shared/base-search-bar/entities/search.entities';
import { ButtonType } from '@design-system/components/molecules/button/entities/button.types';

@Component({
  selector: 'app-travel-assistance',
  templateUrl: './travel-assistance.component.html',
  styleUrls: ['./travel-assistance.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class TravelAssistanceComponent {

  @Input() bookingType: TravelBookingType;
  @Input() isMobile: boolean;
  @Input() assistanceInfo: AssistanceSearchResponse;
  @Input() totalPrice: number;
  @Input() isLoading: boolean;
  @Input() isCrossSelling: boolean;
  @Input() innerViewByCrossSelling: boolean;
  @Input() applyConversion: boolean;
  @Input() conversionRate: number;

  @Input() set showDetailInfo(showDetailInfo: boolean) {
    this._showDetailInfo = showDetailInfo;
    if (this.showDetailInfo && this.isMobile) {
      this._animation = ANIMATIONS.SLIDE_IN_RIGHT;
    }
  }

  @Input() set showAssistanceInfo(showAssistanceInfo: boolean) {
    this._showAssistanceInfo = !!showAssistanceInfo;
    if (this.isMobile) {
      this.domManipulateService.setBodyScroll(!this.showAssistanceInfo);
    }
  }

  @Output() exitEvent: EventEmitter<void> = new EventEmitter<void>();
  @Output() amplitudeTrigger: EventEmitter<AmplitudeEventParams> = new EventEmitter<AmplitudeEventParams>();
  @Output() hiddenAssistanceDetails: EventEmitter<void> = new EventEmitter<void>();
  @Output() payButtonEvent: EventEmitter<{ optionId: number, searchId: string }> = new EventEmitter<{
    optionId: number, searchId: string
  }>();

  public isChecked: boolean;
  public countryCode = ENV.countryInfo.iso_code;
  public showPdf = false;
  public srcPdf: string = ENV.chubb_terms_pdf;

  public readonly declineButtonType = ButtonType.Secondary;

  private _animation: string = ANIMATIONS.SLIDE_IN_RIGHT;
  private _showAssistanceInfo: boolean = false;
  private _showDetailInfo: boolean = false;

  private forcedSelectionYesSelected: boolean = false;
  private forcedSelectionNoSelected: boolean = false;

  constructor(
    private domManipulateService: DomManipulateService,
    private currencyFormatPipe: CurrencyFormatPipe,
    private propertiesAmplitudeService: PropertiesAmplitudeService
  ) { }

  get animation(): string {
    return this.isMobile ? this._animation : null;
  }

  get showAssistanceInfo(): boolean {
    return this._showAssistanceInfo;
  }

  get showDetailInfo(): boolean {
    return this._showDetailInfo;
  }

  get config(): AssetChubbBookingBannerInterface {
    return AssetChubbBookingBanner;
  }

  get resultOption(): AssistanceSearchResult {
    return this.assistanceInfo?.Results?.[0];
  }

  get checkboxPriceMessage(): string {
    let label = this.config.ASSISTANCE.CHECKBOX_MESSAGE_PRICE;
    label = label.replace('{{assistance_price}}', this.price);
    return label;
  }

  get assistanceValues(): AssistanceSearchInclusionDetail[] {
    if (!isNullOrUndefined(this.resultOption)) {
      return assistanceValuesDetail(this.resultOption);
    } else return [];
  }

  get subTitle(): string {
    return this.isCrossSelling ? this.config?.ASSISTANCE?.SUB_TITLE_CROSS_SELLING : this.config?.ASSISTANCE?.SUB_TITLE;
  }

  get currencyCode(): string {
    return this.resultOption?.TotalPrice?.CurrencyCode;
  }

  get basePrice(): number {
    return this.totalPrice;
  }

  get priceWithAssistance(): number {
    return this.totalPrice + this.resultOption?.TotalPrice?.Total;
  }

  get isLoadingForcedSelectionYes(): boolean {
    return this.isLoading && this.forcedSelectionYesSelected;
  }

  get isLoadingForcedSelectionNo(): boolean {
    return this.isLoading && this.forcedSelectionNoSelected;
  }

  get loaderColor(): string {
    return this.isLoadingForcedSelectionYes ? loaderColors.WHITE : loaderColors.GRAY;
  }

  get isBR(): boolean {
    return this.countryCode === Countries.Brazil;
  }

  get isMX(): boolean {
    return this.countryCode === Countries.Mexico;
  }

  get isDisabledForceSelection(): boolean {
    return this.isBR ? !this.isChecked: this.isLoadingForcedSelectionYes;
  }

  get isDisabledForceSelectionNo(): boolean {
    return this.isBR ? this.isChecked: this.isLoadingForcedSelectionYes;
  }

  private get price(): string {
    if (!isNullOrUndefined(this.resultOption?.TotalPrice?.Total)) {
      return this.currencyFormatPipe.transform(this.resultOption?.TotalPrice?.Total, this.currencyCode);
    } else return '';
  }

  private get optionId(): number {
    return this.resultOption?.OptionId;
  }

  public payButtonAction(acceptAssistance: boolean): void {
    if (!this.isLoading) {
      if (acceptAssistance) {
        if (this.isBR && !this.isChecked) {
          return;
        }
        this.forcedSelectionYesSelected = true;
        this.payButtonEvent.emit({ optionId: this.optionId, searchId: this.assistanceInfo?.SearchId });
        this.sendSelectAmplitudeEvent(AVO_EVENTS.SELECT_TRAVEL_ASSISTANCE);
      } else {
        if (this.isDisabledForceSelectionNo) {
          return;
        }
        this.forcedSelectionNoSelected = true;
        this.payButtonEvent.emit({ optionId: null, searchId: null });
      }
      this.close();
    }
  }

  public crossSellingButtonAction(): void {
    if (!this.isLoading) {
      this.payButtonEvent.emit({ optionId: this.optionId, searchId: this.assistanceInfo?.SearchId });
      this.sendSelectAmplitudeEvent(AVO_EVENTS.SELECT_TRAVEL_ASSISTANCE);
    }
  }

  public toggleAcceptAssistance(): void {
    if (!this.isLoading) {
      this.isChecked = !this.isChecked;
    }
  }

  public close(): void {
    if (!this.isLoading) {
      this.exitEvent.emit();
    }
  }

  public closeDetails(): void {
    if (!this.isLoading) {
      if (this.isMobile) {
        this._animation = ANIMATIONS.SLIDE_OUT_RIGHT;
        setTimeout(() => {
          this.hiddenAssistanceDetails.emit();
        }, TIME_TO_SLIDE);
      } else {
        this.hiddenAssistanceDetails.emit();
      }
    }
  }

  public openAssistanceDocument(): void {
    if (this.isMX) {
      this.openPDF();
    }
  }

  public openPDF(): void {
    if (this.isMobile) {
      this.toggleOpenPdfView();
    } else if (typeof window !== 'undefined') {
      window.open(this.srcPdf, '_blank');
    }
  }

  public toggleOpenPdfView(): void {
    this.showPdf = !this.showPdf;
  }

  private sendSelectAmplitudeEvent(type: AVO_EVENTS): void {
    this.propertiesAmplitudeService.searchProperties$(this.getSearchBy()).pipe(
      take(1),
      tap(properties => {
        const props = {
          ...assistanceSearchAmplitudePropsMapper(this.assistanceInfo?.Results?.[0]),
          ...properties
        };
        this.amplitudeTrigger.emit({ type, props });
      }) 
    ).subscribe().unsubscribe();
  }

  private getSearchBy(): SearchBy {
    switch (this.bookingType) {
      case TravelBookingType.BUSES:
        return SearchBy.BUSES;
      case TravelBookingType.AUTOS:
        return SearchBy.AUTOS;
      case TravelBookingType.FLIGHTS:
      case TravelBookingType.FLIGHTS_BY_SECTOR:
        return SearchBy.FLIGHTS;
      case TravelBookingType.HOTELS:
        return SearchBy.HOTELS;
      case TravelBookingType.PACKAGE_AIR:
        return SearchBy.PACKAGES;
      default:
        return SearchBy.FLIGHTS;
    }
  }

}
